import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';

import { corePayeeRelationshipClient } from 'app/api/apiClient/generated/corePayeeRelationshipClient';
import {
  ClaimRelationshipWithoutVerificationRequest,
  CorePayeeRelationshipCurrentRelationshipsParams,
  CreateClaimIntentRequest,
  SuccessResponse,
  UpdateRelationshipRequest2,
} from 'app/api/apiClient/generated/types';
import { useCanViewPayeePromotionalCampaigns, useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { queries } from 'app/constants/queries';
import { useMutationEnhanced } from 'app/hooks/useMutationEnhanced';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';
import {
  useConnectToBrokersSuccessNotification,
} from 'app/pages/payee/UnclaimedRelationships/hooks';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { useClaimRelationshipDispatch } from 'app/state/ClaimRelationshipContext/hooks';
import { getErrorMessages } from 'app/utils/lib/api/getErrorMessages';
import { isPayee, isPayeeAdmin } from 'app/utils/lib/user';

import { CoreApiError } from '../errors';

interface UpdateRelationshipOptions {
  onSuccess?: () => void;
}
interface GetRelationshipOptions {
  enabled?: boolean;
}

export const useClaimRelationshipsForContactMethod = () =>
  useMutationEnhanced(corePayeeRelationshipClient.claimRelationshipsForContactMethod);

export const useGetRelationship = (id: string | undefined,
  options?: GetRelationshipOptions) => {
  const queryFn = useCallback(() => corePayeeRelationshipClient.currentRelationship(id ?? ''),
    [id]);

  return useQueryEnhanced(
    [queries.payeePayeeRelationshipsShow, id],
    queryFn,
    {
      enabled: !!id && (options?.enabled ?? true),
    },
  );
};

export const useGetRelationships = (queryParams?: Partial<CorePayeeRelationshipCurrentRelationshipsParams>) => {
  const user = useGetUser();

  const queryFn = useCallback(() => corePayeeRelationshipClient.currentRelationships({
    exportReport: undefined,
    isQP: undefined,
    page: undefined,
    pageSize: undefined,
    payorId: undefined,
    payorTypeId: undefined,
    sessionCurrencyId: undefined,
    sortAsc: undefined,
    sortField: undefined,
    ...queryParams,
  }),
  [queryParams]);

  return useQueryEnhanced(
    [queries.payeePayeeRelationships, queryParams],
    queryFn,
    {
      enabled: isPayee(user),
    },
  );
};

export const useGetUnclaimedRelationships = () => {
  const user = useGetUser();

  const queryFn = useCallback(() => corePayeeRelationshipClient.unclaimedRelationships({
    exportReport: undefined,
    isQP: undefined,
    page: undefined,
    pageSize: undefined,
    payorId: undefined,
    payorTypeId: undefined,
    sessionCurrencyId: undefined,
    sortAsc: false,
    sortField: 'availableInvoiceAmount',
  }),
  []);

  return useQueryEnhanced(
    [queries.payeeUnclaimedPayeeRelationships],
    queryFn,
    {
      enabled: isPayeeAdmin(user),
    },
  );
};

const useGetRelationshipsByContactInfo = () => {
  const user = useGetUser();

  return useQueryEnhanced(
    [queries.payeeRelationshipsContactInfo],
    corePayeeRelationshipClient.relationshipsByContactInfo,
    {
      enabled: isPayeeAdmin(user),
    },
  );
};

export const useGetAllRelationshipData = () => {
  const {
    data: relationshipData,
    isFetching: isRelationshipDataFetching,
    isInitialLoading: isRelationshipDataLoading,
    status: relationshipDataStatus,
  } = useGetRelationships();
  const {
    data: unclaimedRelationshipData,
    isFetching: isUnclaimedRelationshipDataFetching,
    isInitialLoading: isUnclaimedRelationshipDataLoading,
    status: unclaimedRelationshipDataStatus,
  } = useGetUnclaimedRelationships();
  const {
    data: relationshipsByContactInfoData,
    isFetching: isRelationshipsByContactInfoDataFetching,
    isInitialLoading: isRelationshipsByContactInfoDataLoading,
    status: relationshipsByContactInfoDataStatus,
  } = useGetRelationshipsByContactInfo();

  const isAnyFetching = isRelationshipDataFetching ||
    isUnclaimedRelationshipDataFetching ||
    isRelationshipsByContactInfoDataFetching;

  const isAnyLoading = isRelationshipDataLoading ||
    isUnclaimedRelationshipDataLoading ||
    isRelationshipsByContactInfoDataLoading;

  const isAllSuccess = relationshipDataStatus === 'success' &&
    unclaimedRelationshipDataStatus === 'success' &&
    relationshipsByContactInfoDataStatus === 'success';

  const hasAllData = !!(relationshipData?.payeeRelationships &&
    unclaimedRelationshipData?.payeeRelationships &&
    relationshipsByContactInfoData);

  return useMemo(() => {
    const allFinished = isAllSuccess && hasAllData;

    const claimedRelationships = allFinished ? relationshipData?.payeeRelationships ?? [] : [];
    const unclaimedRelationships = allFinished ? unclaimedRelationshipData?.payeeRelationships ?? [] : [];

    return {
      claimedRelationships,
      hasClaimedRelationships: !!claimedRelationships?.length,
      hasUnclaimedRelationships: !!unclaimedRelationships?.length,
      isFetching: isAnyFetching,
      isInitialLoading: isAnyLoading,
      isLoadingOrFetching: isAnyLoading || isAnyFetching,
      relationshipsByContactInfo: relationshipsByContactInfoData,
      unclaimedRelationships,
    };
  },
  [hasAllData, isAllSuccess, isAnyFetching, isAnyLoading, relationshipData?.payeeRelationships,
    relationshipsByContactInfoData, unclaimedRelationshipData?.payeeRelationships]);
};

export type ClaimRelationshipWithoutVerificationError =
  | null
  | Record<Partial<keyof ClaimRelationshipWithoutVerificationRequest>, string>;

export const useClaimRelationshipWithoutVerification = () => {
  const alertsDispatch = useAlertsDispatch();
  const claimRelationshipDispatch = useClaimRelationshipDispatch();

  const sendSuccessNotification = useConnectToBrokersSuccessNotification();

  const mutationFn = ({
    id,
    payload,
  }: {
    id: string;
    payload: ClaimRelationshipWithoutVerificationRequest;
  }) => corePayeeRelationshipClient.claimRelationshipWithoutVerification(id, payload);

  return useMutationEnhanced<
    AxiosResponse<SuccessResponse>,
    CoreApiError<ClaimRelationshipWithoutVerificationError> | null,
    Parameters<typeof mutationFn>[0]>(
    mutationFn,
    {
      onError: (error, variables) => {
        const brokerAuthorizationFormError = error?.data?.errors;

        if (brokerAuthorizationFormError) {
          claimRelationshipDispatch({
            payload: {
              brokerAuthorizationFormError,
            },
            type: 'setBrokerAuthorizationFormError',
          });

          claimRelationshipDispatch({
            payload: {
              payeeRelationshipId: variables.id,
            },
            type: 'incrementBrokerAuthorizationFormErrorCount',
          });
        } else {
          const errorMessages = getErrorMessages({
            error: error?.data?.errors ?? {},
            expectedKeys: [],
          });

          const [errorMessage] = errorMessages;

          alertsDispatch({
            payload: {
              alert: {
                alertLevel: 'error',
                message: errorMessage,
                title: 'Error',
              },
            },
            type: 'addAlert',
          });
        }
      },
      onMutate: () => {
        claimRelationshipDispatch({
          payload: {
            brokerAuthorizationFormError: null,
          },
          type: 'setBrokerAuthorizationFormError',
        });
      },
      onSuccess: () => {
        sendSuccessNotification(1);
      },
    },
  );
};

export const useGetInvoiceCountForUnclaimedRelationships = () => {
  const {
    data: unclaimedRelationshipData,
  } = useGetUnclaimedRelationships();

  return useMemo(() => {
    return (unclaimedRelationshipData?.payeeRelationships ?? []).reduce((total, relationship) => {
      return total + relationship.availableInvoiceCount;
    },
    0);
  },
  [unclaimedRelationshipData?.payeeRelationships]);
};

export interface UseUpdateRelationshipPayload {
  id: string;
  payload: UpdateRelationshipRequest2;
}

export const useUpdateRelationships = (options?: UpdateRelationshipOptions) => {
  const queryClient = useQueryClient();
  const mutationFn = useCallback(({
    id,
    payload,
  }: UseUpdateRelationshipPayload) => corePayeeRelationshipClient.updateRelationship(id, payload),
  []);

  const mutation = useCallback(async (payloads: UseUpdateRelationshipPayload[]) => {
    const mutations = payloads.map(mutationFn);

    const results = await Promise.all(mutations);

    return {
      data: results,
    };
  },
  [mutationFn]);

  return useMutationEnhanced(mutation,
    {
      invalidateTableQueryPrefixes: [
        queries.payeeInvoicesAndCreditNotes,
        queries.payeePayeeRelationships,
      ],
      onSuccess: data => {
        void queryClient.invalidateQueries([queries.payeeDashboardMyMoney]);
        void queryClient.invalidateQueries([queries.payeeQuickPayableInvoices]);
        void queryClient.invalidateQueries([queries.payeePayeeRelationshipsShow]);

        options?.onSuccess?.();

        // Intentionally calling onSuccess first to prevent any false alarms with Google Analytics
        data.forEach(response => {
          queryClient.setQueryData([queries.payeePayeeRelationshipsShow, response.data.payeeRelationship.id],
            response.data);
        });
      },
    });
};

export const useVerifyPayeeContactMethod = () =>
  useMutationEnhanced(corePayeeRelationshipClient.sendVerificationTokenByContactMethod);

export const useGetPayeeRelationshipPromotionalInvoicesByCampaign = (payeeRelationshipId: string | undefined) => {
  const canViewPromotionalCampaigns = useCanViewPayeePromotionalCampaigns();

  const queryFn = useCallback(() =>
    corePayeeRelationshipClient.promotionalInvoices(payeeRelationshipId ?? ''),
  [payeeRelationshipId]);

  return useQueryEnhanced(
    [queries.payeePromotionalInvoicesByCampaign, payeeRelationshipId],
    queryFn,
    {
      enabled: canViewPromotionalCampaigns && !!payeeRelationshipId,
    },
  );
};

export const useGetActivePromotionalCampaign = (payeeRelationshipId: string | undefined) => {
  const canViewPromotionalCampaigns = useCanViewPayeePromotionalCampaigns();

  const queryFn = useCallback(() =>
    corePayeeRelationshipClient.activePromotionalCampaign(payeeRelationshipId ?? ''),
  [payeeRelationshipId]);

  return useQueryEnhanced(
    [queries.payeeActivePromotionalCampaign, payeeRelationshipId],
    queryFn,
    {
      enabled: canViewPromotionalCampaigns && !!payeeRelationshipId,
    },
  );
};

export const useCreateClaimIntent = () => {
  const mutationFn = useCallback(({
    id,
    payload,
  }: {
    id: string;
    payload: CreateClaimIntentRequest;
  }) => corePayeeRelationshipClient.createClaimIntent(id, payload), []);

  return useMutationEnhanced(mutationFn);
};
